import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faCalculator, faCashRegister, faCreditCard, faDownload, faEuroSign, faMinus, faReceipt } from '@fortawesome/free-solid-svg-icons';

const FinancesPage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const breadcrumb = [{ label: 'Financování' }, { label: 'Finanční přehled' }];

  const financialStats = [
    { title: 'Celkové tržby', value: '156,230 Kč', icon: faEuroSign, color: 'bg-green-50', iconColor: 'text-green-600', change: '+12%', trend: 'up' },
    { title: 'Čistý zisk', value: '89,450 Kč', icon: faArrowUp, color: 'bg-blue-50', iconColor: 'text-blue-600', change: '+8%', trend: 'up' },
    { title: 'Provozní náklady', value: '66,780 Kč', icon: faArrowDown, color: 'bg-red-50', iconColor: 'text-red-600', change: '-3%', trend: 'down' },
    { title: 'Průměrná rezervace', value: '425 Kč', icon: faCalculator, color: 'bg-purple-50', iconColor: 'text-purple-600', change: '+5%', trend: 'up' },
  ];

  const revenueByService = [
    { service: 'Střih a foukaná', amount: 45600, percentage: 29, count: 114 },
    { service: 'Barvení', amount: 38900, percentage: 25, count: 26 },
    { service: 'Kosmetické ošetření', amount: 31200, percentage: 20, count: 26 },
    { service: 'Manikúra', amount: 23400, percentage: 15, count: 39 },
    { service: 'Holení', amount: 17130, percentage: 11, count: 43 },
  ];

  const expenses = [
    { category: 'Platy zaměstnanců', amount: 45000, percentage: 67 },
    { category: 'Materiál a produkty', amount: 12000, percentage: 18 },
    { category: 'Pronájem', amount: 8500, percentage: 13 },
    { category: 'Energie a služby', amount: 2500, percentage: 4 },
  ];

  const recentTransactions = [
    { date: '2024-11-15', type: 'income', description: 'Rezervace - Jana Nováková', amount: 800, method: 'Hotovost' },
    { date: '2024-11-15', type: 'income', description: 'Rezervace - Petr Svoboda', amount: 1200, method: 'Karta' },
    { date: '2024-11-15', type: 'expense', description: 'Nákup materiálu', amount: -2500, method: 'Převod' },
    { date: '2024-11-14', type: 'income', description: 'Rezervace - Marie Krásná', amount: 1500, method: 'Karta' },
    { date: '2024-11-14', type: 'expense', description: 'Plat za energie', amount: -1800, method: 'Převod' },
  ];

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return faArrowUp;
      case 'down':
        return faArrowDown;
      default:
        return faMinus;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getTransactionIcon = (type: string) => {
    return type === 'income' ? faCashRegister : faReceipt;
  };

  const getTransactionColor = (type: string) => {
    return type === 'income' ? 'text-green-600' : 'text-red-600';
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Finanční přehled" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Finanční přehled</h1>
                <p className="text-gray-600 mt-1">Přehled příjmů, výdajů a ziskovosti</p>
              </div>
              <div className="flex items-center space-x-3">
                <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="week">Tento týden</option>
                  <option value="month">Tento měsíc</option>
                  <option value="quarter">Toto čtvrtletí</option>
                  <option value="year">Tento rok</option>
                </select>
                <button className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Export
                </button>
              </div>
            </div>
          </section>

          {/* Financial Stats */}
          <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {financialStats.map((stat, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6 hover:transform hover:-translate-y-1 transition-transform">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`p-3 rounded-lg ${stat.color}`}>
                      <FontAwesomeIcon icon={stat.icon} className={`text-xl ${stat.iconColor}`} />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                      <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={getTrendIcon(stat.trend)} className={`w-4 h-4 mr-1 ${getTrendColor(stat.trend)}`} />
                      <p className={`text-sm font-medium ${getTrendColor(stat.trend)}`}>{stat.change}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue by Service */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Tržby podle služeb</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {revenueByService.map((item, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900">{item.service}</span>
                        <span className="text-sm text-gray-600">{item.amount.toLocaleString()} Kč</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-600 h-2 rounded-full transition-all duration-300" style={{ width: `${item.percentage}%` }}></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{item.count} služeb</span>
                        <span>{item.percentage}% z celku</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Expenses Breakdown */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Rozdělení výdajů</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {expenses.map((expense, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900">{expense.category}</span>
                        <span className="text-sm text-gray-600">{expense.amount.toLocaleString()} Kč</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-red-600 h-2 rounded-full transition-all duration-300" style={{ width: `${expense.percentage}%` }}></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{expense.percentage}% z výdajů</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Transactions */}
          <section className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Nedávné transakce</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Popis</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Způsob platby</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Částka</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentTransactions.map((transaction, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(transaction.date).toLocaleDateString('cs-CZ')}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FontAwesomeIcon icon={getTransactionIcon(transaction.type)} className={`w-4 h-4 mr-2 ${getTransactionColor(transaction.type)}`} />
                          <span className="text-sm text-gray-900">{transaction.description}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600">
                          <FontAwesomeIcon icon={transaction.method === 'Karta' ? faCreditCard : faCashRegister} className="w-4 h-4 mr-2 text-gray-400" />
                          {transaction.method}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-medium ${getTransactionColor(transaction.type)}`}>
                          {transaction.amount > 0 ? '+' : ''}
                          {transaction.amount.toLocaleString()} Kč
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Quick Actions */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rychlé akce</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                <FontAwesomeIcon icon={faReceipt} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Přidat výdaj</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <FontAwesomeIcon icon={faCashRegister} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Přidat příjem</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                <FontAwesomeIcon icon={faCalculator} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Daňový přehled</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors">
                <FontAwesomeIcon icon={faDownload} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Export účetnictví</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default FinancesPage;
